// src/components/BoardPackConfiguration/pages/ThirdTable.jsx
import React, { useState, useEffect, useRef } from "react";
import { apiCall } from "@/api/apiCall";
import LoadingComponent from "@/components/common/LoadingComponent";
import ErrorState from "@/components/common/ErrorStateComponent";
import { useQueue } from "@/context/QueueContext";
import NoDataComponent from "@/components/common/NoDataComponent";
import HeadingComponent from "@/components/BoardPack/components/shared/heading";
import CfoCommentsSmallTable from "@/components/BoardPack/components/tables/CfoCommentsSmallTable";

const ThirdTable = ({
  year = "",
  month = "",
  monthNumber = "",
  contract = "",
  page = null,
  isExporting = false,       // ✅ export mode
  cfoComment = "",           // ✅ lifted state from BoardPack
  onCfoCommentChange,        // ✅ setter from BoardPack
}) => {
  const [tableData, setTableData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasFetchedRef = useRef(false);

  const { enqueue } = useQueue();

  const is_cfo = page?.params?.is_cfo ? true : false;
  const measure = page?.params?.measure ? page?.params?.measure : 0;
  const value_type = page?.params?.value_type ? page?.params?.value_type : 0;

  useEffect(() => {
    // if (!year || !month || !contract) return;
    // if (hasFetchedRef.current) return;
    // hasFetchedRef.current = true;
    loadData();
  }, [year, month, contract]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    setSummary(null);

    try {
      const response = {
    "success": true,
    "errorCode": 0,
    "message": "data fetched successfully",
    "meta": {
        "contract": "0020018",
        "year": 2025,
        "month": "Dec",
        "prevMonth": "Nov",
        "action": "CNS",
        "value_type": 4,
        "measure": 1
    },
    "mdx": "SELECT { ([Year].[2025], [Months].[Dec], [Version].[Actual]), ([Year].[2025], [Months].[Nov], [Version].[Actual]), ([Year].[2025], [Months].[Dec], [Version].[Budget]), ([Year].[2024], [Months].[Dec], [Version].[Actual]) } ON COLUMNS, { [Enforcement_KPI_Measures].[Net Count], [Enforcement_KPI_Measures].[Average Jobs Per day], [Enforcement_KPI_Measures].[EOs/Drivers Employed], [Enforcement_KPI_Measures].[EOS/Drivers Deployed], [Enforcement_KPI_Measures].[% Deployed to Employed], [Enforcement_KPI_Measures].[Average CNs Per Officer Per Day], [Enforcement_KPI_Measures].[Average Value of CN] } ON ROWS FROM [KPI_Enforcement] WHERE ( [Payment Method Way].[Total Payment Method Way], [Employee].[Total Employee], [CC_Contract].[0020018], [Action].[CNS] )",
    "data": {
        "month": "Dec",
        "prevMonth": "Nov",
        "year": 2025,
        "prevYear": 2024,
        "rows": {
            "Net Count": {
                "actual": "986",
                "priorMonth": "927",
                "varPm": "59",
                "varPmPct": "6.36%",
                "budget": "-",
                "varBudget": "-",
                "varBudgetPct": "-",
                "priorYear": "1,285",
                "varPy": "(299)",
                "varPyPct": "(23.27%)"
            },
            "Average Jobs Per day": {
                "actual": "32",
                "priorMonth": "31",
                "varPm": "1",
                "varPmPct": "2.93%",
                "budget": "-",
                "varBudget": "-",
                "varBudgetPct": "-",
                "priorYear": "41",
                "varPy": "(10)",
                "varPyPct": "(23.27%)"
            },
            "EOs/Drivers Employed": {
                "actual": "8",
                "priorMonth": "8",
                "varPm": "0",
                "varPmPct": "0.00%",
                "budget": "-",
                "varBudget": "-",
                "varBudgetPct": "-",
                "priorYear": "9",
                "varPy": "(1)",
                "varPyPct": "(11.11%)"
            },
            "EOS/Drivers Deployed": {
                "actual": "6",
                "priorMonth": "7",
                "varPm": "(1)",
                "varPmPct": "(14.29%)",
                "budget": "-",
                "varBudget": "-",
                "varBudgetPct": "-",
                "priorYear": "6",
                "varPy": "0",
                "varPyPct": "0.00%"
            },
            "% Deployed to Employed": {
                "actual": "75.00%",
                "priorMonth": "87.50%",
                "varPm": "0",
                "varPmPct": "(14.29%)",
                "budget": "-",
                "varBudget": "-",
                "varBudgetPct": "-",
                "priorYear": "66.67%",
                "varPy": "0",
                "varPyPct": "12.50%"
            },
            "Average CNs Per Officer Per day": {
                "actual": "5",
                "priorMonth": "4",
                "varPm": "1",
                "varPmPct": "20.09%",
                "budget": "-",
                "varBudget": "-",
                "varBudgetPct": "-",
                "priorYear": "7",
                "varPy": "(2)",
                "varPyPct": "(23.27%)"
            },
            "Average Value of CN": {
                "actual": "300",
                "priorMonth": "294",
                "varPm": "5",
                "varPmPct": "1.76%",
                "budget": "-",
                "varBudget": "-",
                "varBudgetPct": "-",
                "priorYear": "245",
                "varPy": "55",
                "varPyPct": "22.46%"
            }
        }
    },
    "is_cfo": true,
    "is_ai": true,
    "ai_prompt": "You are a CFO financial analyst.\n\nCONTEXT:\n- Contract: 0020018\n- Year: 2025\n- Month: Dec\n\nTASK:\nWrite a SHORT KPI summary (Enforcement Operations).\n\nNON-NEGOTIABLE OUTPUT FORMAT:\n- Output MUST contain BETWEEN 6 AND 7 bullet LINES.\n- Each bullet MUST be a SEPARATE LINE.\n- Each bullet MUST start with '- ' (dash then space).\n- DO NOT use numbering like 1), 2), 3) or 1. 2. 3.\n- DO NOT add any title or paragraph before/after bullets.\n- Do NOT use colons ':' inside bullets.\n\nBREVITY RULES (IMPORTANT):\n- Each bullet MUST be max 16 words.\n- Each bullet MUST be ONE sentence only.\n- Avoid detailed explanations.\n- Focus on high-level KPIs only.\n\nCONTENT RULES:\n- First bullet MUST mention Contract (0020018), Year (2025), Month (Dec).\n- Use ONLY labels present in the JSON.\n- Prefer these labels when available: Count, Net Count, Revenue, Escalation Rate, CN Paid Rate, Average Value of CN, EOs/Drivers Employed, EOS/Drivers Deployed, % Deployed to Employed, Average Jobs Per day.\n- Do NOT include raw Budget values; mention variance direction only when needed.\n- Do NOT use the word 'Block'.\n\nNUMBER FORMAT RULES (STRICT):\n- BEFORE writing bullets, convert ALL numeric values:\n  - ≥1,000,000 → M (one decimal max)\n  - ≥1,000 → K (one decimal max)\n  - <1,000 → integer only\n- Raw numbers like 4949075 are STRICTLY FORBIDDEN.\n- Do NOT mix raw numbers with K/M/B.\n- Do NOT add currency symbols (no $, SAR, USD).\n- Values like (227,872) represent NEGATIVE numbers.\n\nINVALID EXAMPLE:\n- Total Revenue was 4949075.\n\nVALID EXAMPLE:\n- Total Revenue was 4.95M.\n\nSELF-CHECK (MANDATORY):\n- If ANY bullet violates rules, output EXACTLY: ERROR_RULES_NOT_MET\n\nIMPORTANT:\n- Any rule violation will cause the output to be rejected by an automated validator.\n\nRAW_DATA_JSON:\n{\"meta\":{\"contract\":\"0020018\",\"year\":\"2025\",\"month\":\"Dec\",\"prevMonth\":\"Nov\",\"action\":\"CNS\",\"value_type\":4,\"type\":\"enforcement_headline\"},\"data\":[{\"label\":\"Net Count\",\"actual\":\"986\",\"priorMonth\":\"927\",\"varPmPct\":\"6.36%\",\"priorYear\":\"1,285\",\"varPyPct\":\"(23.27%)\"},{\"label\":\"Average Jobs Per day\",\"actual\":\"32\",\"priorMonth\":\"31\",\"varPmPct\":\"2.93%\",\"priorYear\":\"41\",\"varPyPct\":\"(23.27%)\"},{\"label\":\"EOs\\/Drivers Employed\",\"actual\":\"8\",\"priorMonth\":\"8\",\"varPmPct\":\"0.00%\",\"priorYear\":\"9\",\"varPyPct\":\"(11.11%)\"},{\"label\":\"EOS\\/Drivers Deployed\",\"actual\":\"6\",\"priorMonth\":\"7\",\"varPmPct\":\"(14.29%)\",\"priorYear\":\"6\",\"varPyPct\":\"0.00%\"},{\"label\":\"% Deployed to Employed\",\"actual\":\"75.00%\",\"priorMonth\":\"87.50%\",\"varPmPct\":\"(14.29%)\",\"priorYear\":\"66.67%\",\"varPyPct\":\"12.50%\"},{\"label\":\"Average CNs Per Officer Per day\",\"actual\":\"5\",\"priorMonth\":\"4\",\"varPmPct\":\"20.09%\",\"priorYear\":\"7\",\"varPyPct\":\"(23.27%)\"},{\"label\":\"Average Value of CN\",\"actual\":\"300\",\"priorMonth\":\"294\",\"varPmPct\":\"1.76%\",\"priorYear\":\"245\",\"varPyPct\":\"22.46%\"}]}",
    "ai_summary": "- Contract 0020018, Dec 2025 KPIs (Enforcement Operations):\n- Net Count: 986 (6.36% increase vs Nov)\n- Average Jobs Per day: 32 (2.93% increase vs Nov)\n- EOs/Drivers Employed: 8 (-11.11% vs previous year)\n- EOS/Drivers Deployed: 6 (14.29% decrease vs Nov)\n- % Deployed to Employed: 75.00% (12.50% increase vs previous year)\n- Average CNs Per Officer Per day: 5 (20.09% increase vs Nov)\n- Average Value of CN: 300 (22.46% increase vs previous year)",
    "lines": [
        "- Contract 0020018, Dec 2025 KPIs (Enforcement Operations):",
        "- Net Count: 986 (6.36% increase vs Nov)",
        "- Average Jobs Per day: 32 (2.93% increase vs Nov)",
        "- EOs/Drivers Employed: 8 (-11.11% vs previous year)",
        "- EOS/Drivers Deployed: 6 (14.29% decrease vs Nov)",
        "- % Deployed to Employed: 75.00% (12.50% increase vs previous year)",
        "- Average CNs Per Officer Per day: 5 (20.09% increase vs Nov)",
        "- Average Value of CN: 300 (22.46% increase vs previous year)"
    ]
}

      if (!response?.success) {
        throw new Error(response?.message || "API returned unsuccessful response");
      }
      setTableData(response.data);
      setSummary({
        ai_summary: response?.ai_summary,
        lines: response?.lines,
      });
    } catch (err) {
      console.error("Error loading data:", err);
      setError(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingComponent />;
  if (error) return <ErrorState error={error} onRetry={loadData} />;
  // if(tableData?.rows){
  // return <NoDataComponent  onRetry={loadData} />
  // }

  const title = page?.title || "CFO Comments"
  const subtitle = page?.subtitle

  return (
    <div className="min-h-screen flex relative w-full bg-bg-bw-custom">
      <div className="flex-1 flex flex-col px-6 pt-6">
        {/* Heading */}
        {/* <div className="mb-9">
          <h1 className="text-md text-center font-semibold text-[#001F5F]">
            {page?.title ? page?.title : "CFO Comments"}
          </h1>
        </div> */}

        <div className="flex flex-col gap-1">
        {
          title && <HeadingComponent variant={is_cfo ? "cfo" : "main"}>
            {title}
          </HeadingComponent>
        }
        {
          subtitle && <HeadingComponent variant={is_cfo ? "cfoSub" : "sub"}>
            {subtitle}
          </HeadingComponent>
        }
      </div>

        {/* Text Content */}
        <div className="text-dark-text-custom w-full mb-2 text-[15px]">
          {page?.first_heading && (
            <ul className="list-disc ml-12 space-y-1">
              <li>{page?.first_heading}</li>
            </ul>
          )}
          {page?.second_heading_Bold && (
            <p className="font-bold ml-6 mt-2">{page?.second_heading_Bold}</p>
          )}
          {page?.third_hedaing && (
            <ul className="list-disc ml-12 mt-1">
              <li>{page?.third_hedaing}</li>
            </ul>
          )}
        </div>

        {/* Table — cfoComment + onCfoCommentChange + isExporting pass karo */}
        <CfoCommentsSmallTable
          data={tableData}
          summary={summary}
          loading={loading}
          error={error}
          headerTitle={page?.headerTitle}
          subHeaderTitle={page?.subheaderTitle}
          month={tableData?.month}
          prevMonth={tableData?.prevMonth}
          isExporting={isExporting}
          cfoComment={cfoComment}
          onCfoCommentChange={onCfoCommentChange}
          is_cfo={is_cfo}
        />
      </div>
    </div>
  );
};

export default ThirdTable;